import { useIntl } from 'react-intl';
import { z } from 'zod';

import { getPlainTextLength } from '@/utils/string-util';

import { MIN_JOB_DESCRIPTION_LENGTH } from './constants';

const JOB_TYPE_VALUES = [
  'permanent',
  'temporary',
  'contract_freelancer',
  'casual',
  'finance_plus_accounting',
  'operations_leadership',
  'all_other_administrative_plus_professional',
  'information_technology',
  'customer_service_plus_operations',
  'local',
  'regional',
  'trainee',
  'shuttle',
] as const;

const baseFields = {
  title: z.string(),
  description: z.string(),
  industry: z.string().nullable(),
  jobHours: z.enum(['full_time', 'part_time']).nullable(),
  jobType: z.enum(JOB_TYPE_VALUES).nullable(),
  experience: z.number().nullable(),
  locationAddress: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullish(),
  remote: z.boolean().optional(),
};

/**
 * Hook to create the validation schema for job details (title, description, states).
 *
 * Field-level always-required: `title`, `description`.
 *
 * Conditionally required (validated per-opening via `superRefine`):
 * - `stateCode`: required when the opening is remote AND ZipRecruiter is selected.
 * - Server-managed fields (`industry`, `jobHours`, `jobType`, `experience`,
 *   `locationAddress`, `postalCode`, `city`): required only when the opening is
 *   selected by at least one Vonq channel (i.e. its id appears in some
 *   `channels[k].openingBudgets` where `channels[k].vonqDetails != null`).
 *
 * The schema is built as `z.object(vonqShape).catchall(baseSchema)` instead of
 * `z.record().superRefine()` so that Vonq-required checks fire when
 * `trigger('jobDetails.<openingId>')` validates a single opening (e.g. on Save).
 *
 * @param isZipRecruiterSelected - Passed from the form's parent so the schema can
 *   validate `stateCode` without reaching into context.
 * @param vonqSelectedOpeningIds - Set of opening ids that are present in any Vonq
 *   channel's `openingBudgets`. Drives conditional required-checks for the
 *   server-managed fields above.
 */
export function useJobDetailsSchema(
  isZipRecruiterSelected = false,
  vonqSelectedOpeningIds: ReadonlySet<string> = new Set(),
) {
  const intl = useIntl();

  const messages = {
    titleRequired: intl.formatMessage({
      defaultMessage: 'Title is required',
      description: 'Title input required error message',
    }),
    descriptionRequired: intl.formatMessage({
      defaultMessage: 'Description is required',
      description: 'Description input required error message',
    }),
    descriptionMinLength: intl.formatMessage({
      defaultMessage: 'Description must be at least 250 characters long',
      description: 'Description input validation error message',
    }),
    stateCodeRequired: intl.formatMessage({
      defaultMessage: 'Hiring State is required',
      description:
        'State code required when ZipRecruiter is selected for remote openings',
    }),
    industryRequired: intl.formatMessage({
      defaultMessage: 'Industry is required',
      description:
        'Validation error shown when the opening has no industry set',
    }),
    jobHoursRequired: intl.formatMessage({
      defaultMessage: 'Job hours is required',
      description:
        'Validation error shown when the opening has no job hours (full-time/part-time) set',
    }),
    jobTypeRequired: intl.formatMessage({
      defaultMessage: 'Job type is required',
      description:
        'Validation error shown when the opening has no job type (permanent, temporary, etc.) set',
    }),
    experienceRequired: intl.formatMessage({
      defaultMessage: 'Years of experience is required',
      description:
        'Validation error shown when the opening has no years-of-experience value set',
    }),
    locationRequired: intl.formatMessage({
      defaultMessage: 'Location is required',
      description:
        'Validation error shown when the opening has no physical location address set',
    }),
    postalCodeRequired: intl.formatMessage({
      defaultMessage: 'Postal code is required',
      description:
        'Validation error shown when the opening has no postal/ZIP code set',
    }),
    cityRequired: intl.formatMessage({
      defaultMessage: 'City is required',
      description:
        'Validation error shown when the opening has no city set',
    }),
  };

  /**
   * Build a per-opening schema with a `superRefine` that handles both the
   * always-required fields (title, description) and the conditional checks
   * (stateCode for ZipRecruiter, Vonq-required server-managed fields).
   *
   * Because each Vonq opening gets its own schema instance via the
   * `z.object(vonqShape)` below, `trigger('jobDetails.<id>')` fires this
   * `superRefine` for a single opening — matching the Save-button behavior.
   */
  const makeOpeningSchema = (isVonqOpening: boolean) =>
    z.object(baseFields).superRefine((detail, ctx) => {
      if (!detail.title.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: messages.titleRequired,
          path: ['title'],
        });
      }

      if (!detail.description.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: messages.descriptionRequired,
          path: ['description'],
        });
      } else if (
        getPlainTextLength(detail.description) < MIN_JOB_DESCRIPTION_LENGTH
      ) {
        ctx.addIssue({
          code: 'custom',
          message: messages.descriptionMinLength,
          path: ['description'],
        });
      }

      if (
        isZipRecruiterSelected &&
        detail.remote === true &&
        (!detail.stateCode || !String(detail.stateCode).trim())
      ) {
        ctx.addIssue({
          code: 'custom',
          message: messages.stateCodeRequired,
          path: ['stateCode'],
        });
      }

      if (!isVonqOpening) return;

      if (!detail.industry || !detail.industry.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: messages.industryRequired,
          path: ['industry'],
        });
      }
      if (detail.jobHours === null) {
        ctx.addIssue({
          code: 'custom',
          message: messages.jobHoursRequired,
          path: ['jobHours'],
        });
      }
      if (detail.jobType === null) {
        ctx.addIssue({
          code: 'custom',
          message: messages.jobTypeRequired,
          path: ['jobType'],
        });
      }
      if (detail.experience === null || detail.experience < 0) {
        ctx.addIssue({
          code: 'custom',
          message: messages.experienceRequired,
          path: ['experience'],
        });
      }
      if (!detail.locationAddress || !detail.locationAddress.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: messages.locationRequired,
          path: ['locationAddress'],
        });
      }
      if (!detail.postalCode || !detail.postalCode.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: messages.postalCodeRequired,
          path: ['postalCode'],
        });
      }
      if (!detail.city || !detail.city.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: messages.cityRequired,
          path: ['city'],
        });
      }
    });

  const vonqShape: Record<string, z.ZodOptional<z.ZodType>> = {};
  for (const openingId of vonqSelectedOpeningIds) {
    vonqShape[openingId] = makeOpeningSchema(true).optional();
  }

  return z.object(vonqShape).catchall(makeOpeningSchema(false));
}
